import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  inject,
  Injector
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { _HttpClient, SettingsService } from '@delon/theme';
import { DataEditActionsComponent } from '@shared-feature/components/actions/data-edit-actions/data-edit-actions.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { finalize } from 'rxjs/operators';
import { FormService } from 'src/app/core/util/form.service';
import { SstReturnService } from 'src/app/routes/tax/sstreturn/sstreturn.service';
import { DateFormatService } from 'src/app/shared/services/date-format.service';

@Component({
  selector: 'app-sst-return-declaration-info-modal',
  templateUrl: './sst-return-declaration-info-modal.component.html',
  styleUrls: ['./sst-return-declaration-info-modal.component.less'],
  standalone: false
})
export class SSTReturnDeclarationInfoModalComponent
  implements OnInit, OnChanges
{
  // Inject the base service using forwardRef, but make it optional since it might be used from the list component
  private dataEditActionsComponent: DataEditActionsComponent | null = inject(
    DataEditActionsComponent,
    { optional: true }
  );

  @Input() isVisible = {
    submit: false,
    unlock: false
  };
  @Input() status: boolean = false;
  @Output() readonly isVisibleChange = new EventEmitter<{
    submit: boolean;
    unlock: boolean;
  }>();
  @Input() id: number = 0;
  dateFormat: string;
  observeChanges = false;
  modified = false;
  // Add this property to your component class
  form!: FormGroup;

  // Store the last form values to preserve between openings
  private lastFormValues: any = null;
  private lastVisibleState = { submit: false, unlock: false };

  constructor(
    private modal: NzModalService,
    private http: _HttpClient,
    public fs: FormService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private msg: NzMessageService,
    private settings: SettingsService,
    private router: Router,
    private dateFormatService: DateFormatService,
    private injector: Injector
  ) {
    this.dateFormat = this.dateFormatService.currentDateFormatValue;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update the form ID when the input ID changes
    if (changes['id'] && this.form) {
      this.form.get('id')?.setValue(this.id);
    }

    // Update the form status validators when the status changes
    if (changes['status'] && this.form) {
      if (this.status) {
        this.form.controls['name'].setValidators([]);
        this.form.controls['identityNo'].setValidators([]);
        this.form.controls['designation'].setValidators([]);
        this.form.controls['phoneNo'].setValidators([]);

        this.form.controls['name'].updateValueAndValidity();
        this.form.controls['identityNo'].updateValueAndValidity();
        this.form.controls['designation'].updateValueAndValidity();
        this.form.controls['phoneNo'].updateValueAndValidity();
      } else {
        this.form.controls['name'].setValidators([Validators.required]);
        this.form.controls['identityNo'].setValidators([Validators.required]);
        this.form.controls['designation'].setValidators([Validators.required]);
        this.form.controls['phoneNo'].setValidators([Validators.required]);

        this.form.controls['name'].updateValueAndValidity();
        this.form.controls['identityNo'].updateValueAndValidity();
        this.form.controls['designation'].updateValueAndValidity();
        this.form.controls['phoneNo'].updateValueAndValidity();
      }
    }

    // Update isVisible if needed
    if (changes['isVisible']) {
      // Check if the modal is being opened (was previously closed)
      const isOpening =
        (this.isVisible.submit || this.isVisible.unlock) &&
        !this.lastVisibleState.submit &&
        !this.lastVisibleState.unlock;

      // If the modal is becoming visible, make sure the form ID is set correctly
      if (isOpening && this.form) {
        // Always update the ID
        this.form.get('id')?.setValue(this.id);

        // If we have saved form values and it's the same ID, restore them
        if (this.lastFormValues && this.lastFormValues.id === this.id) {
          this.loadLatestDeclarationInfo();
        }
      }

      // Set validator for notes field when unlock modal is shown
      if (this.form) {
        if (this.isVisible.unlock) {
          this.form.controls['notes'].setValidators([Validators.required]);
        } else {
          this.form.controls['notes'].setValidators([]);
        }
        this.form.controls['notes'].updateValueAndValidity();
      }

      // Save the current visibility state for next comparison
      this.lastVisibleState = { ...this.isVisible };
    }
  }

  ngOnInit(): void {
    // Create the form first
    this.createForm();
    this.loadLatestDeclarationInfo();
  }

  loadLatestDeclarationInfo() {
    this.http
      .get('api/sstreturns/GetLatestDeclarationInfo?_silent=true')
      .subscribe((res: any) => {
        if (res) {
          // Only set these values if we don't have stored values
          if (!this.lastFormValues) {
            this.form.controls['name'].setValue(res.declarantName);
            this.form.controls['identityNo'].setValue(res.declarantIdentityNo);
            this.form.controls['designation'].setValue(
              res.declarantDesignation
            );
            this.form.controls['phoneNo'].setValue(res.declarantPhoneNo);
          } else {
            // We have stored values, restore them
            this.restoreFormValues();
          }
        }

        if (this.status) {
          this.form.controls['name'].setValidators([]);
          this.form.controls['identityNo'].setValidators([]);
          this.form.controls['designation'].setValidators([]);
          this.form.controls['phoneNo'].setValidators([]);

          this.form.controls['name'].updateValueAndValidity();
          this.form.controls['identityNo'].updateValueAndValidity();
          this.form.controls['designation'].updateValueAndValidity();
          this.form.controls['phoneNo'].updateValueAndValidity();
        }
      });
  }

  createForm() {
    // Change this to assign to this.form instead of a local variable
    this.form = this.fb.group({
      id: [this.id],
      date: [new Date(), [Validators.required]],
      name: ['', [Validators.required]],
      identityNo: ['', [Validators.required]],
      designation: ['', [Validators.required]],
      phoneNo: ['', [Validators.required]],
      status: [true, []],
      notes: ['']
    });

    this.form.controls['date'].valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.form.controls['date'].setValue(val);
    });

    this.form.controls['name'].valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls['name'].setValue(val);
    });

    this.form.controls['identityNo'].valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls['identityNo'].setValue(val);
    });

    this.form.controls['designation'].valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls['designation'].setValue(val);
    });

    this.form.controls['phoneNo'].valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls['phoneNo'].setValue(val);
    });

    this.form.controls['notes'].valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.form.controls['notes'].setValue(val);
    });

    this.form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    return this.form;
  }

  // Helper method to save current form values
  private saveFormValues() {
    if (this.form) {
      this.lastFormValues = { ...this.form.value };
    }
  }

  // Helper method to restore form values
  private restoreFormValues() {
    if (this.lastFormValues && this.form) {
      // Preserve the current ID
      const currentId = this.id;

      // Disable validators temporarily to avoid validation errors during restore
      this.observeChanges = false;

      // Update form with saved values but keep current ID
      this.form.patchValue({
        ...this.lastFormValues,
        id: currentId
      });

      // Re-enable validators
      this.observeChanges = true;
    }
  }

  // Clear saved form data (use after successful submission)
  private clearSavedFormData() {
    this.lastFormValues = null;
  }
  submitSST() {
    this.fs.validateAsync(this.form).then(isValid => {
      this.cdr.detectChanges();
      if (!isValid) {
        const invalidControls = this.fs.getInvalidControls(this.form);
        this.fs.showValidationMsg(invalidControls);
        return;
      }

      const value = {
        id: this.id,
        declarationDate: this.form.value.date,
        declarantName: this.form.value.name,
        declarantIdentityNo: this.form.value.identityNo,
        declarantDesignation: this.form.value.designation,
        declarantPhoneNo: this.form.value.phoneNo,
        status: true,
        notes: null
      };

      // Show confirmation modal for journal generation
      this.modal.confirm({
        nzTitle: 'Generate Journals',
        nzContent:
          'SST Return, Deemed payment and Deemed Payment Recovery journals Will be created after submit. Would you like to proceed?',
        nzOkText: 'Yes',
        nzCancelText: 'No',
        nzClosable: true,
        nzMaskClosable: false,
        nzOnOk: () => {
          // Show loading message
          const loadingMsg = this.msg.loading('Generating journals...', {
            nzDuration: 0
          });

          // First update declaration info
          this.http
            .post('api/sstreturns/UpdateDeclarationInfo', value)
            .pipe(
              finalize(() => {
                this.msg.remove(loadingMsg.messageId);
              })
            )
            .subscribe({
              next: () => {
                this.clearSavedFormData();
                this.isVisible.submit = false;

                // Call reload only if dataEditActionsComponent exists (in detail view)
                if (this.dataEditActionsComponent) {
                  this.dataEditActionsComponent.reload();
                }

                // Always trigger event to refresh attachments
                const sstReturnService = this.injector.get(SstReturnService);
                sstReturnService.eventTrigger.next({
                  id: value.id
                });

                this.isVisibleChange.emit(this.isVisible);
                this.msg.success(
                  'SST Return submitted successfully with journals generated'
                );
              },

              //   next: () => {
              //     // After declaration info is updated, generate journals

              //     // Get the SST return data to generate journals
              //     this.http.get(`api/sstreturns/${this.id}`).subscribe({
              //       next: (sstReturn: any) => {
              //         // Create SST Return Journal first
              //         this.http
              //           .post(`api/journals/CreateSSTReturnJounal/${this.id}`, {})
              //           .subscribe({
              //             next: () => {
              //               // After SST Return Journal is created, generate and attach reports
              //               this.http
              //                 .post(
              //                   `api/sstreturns/GenerateAndAttachReports/${this.id}`,
              //                   {}
              //                 )
              //                 .subscribe({
              //                   next: () => {
              //                     this.clearSavedFormData();
              //                     this.isVisible.submit = false;

              //                     // Call reload only if dataEditActionsComponent exists (in detail view)
              //                     if (this.dataEditActionsComponent) {
              //                       this.dataEditActionsComponent.reload();
              //                     }

              //                     // Always trigger event to refresh attachments
              //                     const sstReturnService =
              //                       this.injector.get(SstReturnService);
              //                     sstReturnService.eventTrigger.next({
              //                       id: value.id
              //                     });

              //                     this.isVisibleChange.emit(this.isVisible);
              //                     this.msg.success(
              //                       'SST Return submitted successfully with journals generated'
              //                     );
              //                   },
              //                   error: (err: any) => {
              //                     // For other errors, show the generic error message
              //                     this.msg.error(
              //                       'Failed to generate reports. Please try again later.'
              //                     );
              //                   }
              //                 });
              //             },
              //             error: (err: any) => {
              //               // Check if the error is related to missing SST settings
              //               if (
              //                 (err.error && err.error.message === 'SST settings are missing or incomplete.') || (err.error && err.error.code === 'ERR_MISSING_SST_SETTINGS')
              //               ) {
              //                 this.isVisible.submit = false;
              //                 this.cdr.detectChanges();
              //                 this.router.navigate(['./tax/sstsettings']);
              //               } else {
              //                 // For other errors, show the generic error message
              //                 this.msg.error(
              //                   'Failed to generate journals. Please try again later.'
              //                 );
              //               }
              //             }
              //           });
              //     },
              //     error: err => {
              //       this.msg.error(
              //         'Failed to get SST Return data. Please try again later.'
              //       );
              //     }
              //   });
              // },
              error: (err: any) => {
                // Check if the error is related to missing SST settings
                if (
                  (err.error &&
                    err.error.message ===
                      'SST settings are missing or incomplete.') ||
                  (err.error && err.error.code === 'ERR_MISSING_SST_SETTINGS')
                ) {
                  this.isVisible.submit = false;
                  this.cdr.detectChanges();
                  this.router.navigate(['./tax/sstsettings']);
                } else {
                  // For other errors, show the generic error message
                  this.msg.error(
                    'Failed to submit SST Return. Please try again later.'
                  );
                }
              }
            });
        }
      });
    });
  }

  unlockSST() {
    this.fs.validateAsync(this.form).then(isValid => {
      this.cdr.detectChanges();
      if (!isValid) {
        const invalidControls = this.fs.getInvalidControls(this.form);
        this.fs.showValidationMsg(invalidControls);
        return;
      }

      const value = {
        id: this.id,
        declarationDate: null,
        declarantName: null,
        declarantIdentityNo: null,
        declarantDesignation: null,
        declarantPhoneNo: '0',
        status: false,
        notes: this.form.value.notes
      };

      this.modal.confirm({
        nzTitle: 'Unlock SST Return',
        nzContent:
          'Unlocking SST will clear declaration information and remove Journal Entries for SST Payable. <br> Are you sure you want to proceed?',
        nzOkText: 'Yes',
        nzCancelText: 'No',
        nzOnOk: () => {
          // Show loading message
          const loadingMsg = this.msg.loading('Unlocking SST Return...', {
            nzDuration: 0
          });

          this.http
            .post('api/sstreturns/UpdateDeclarationInfo', value)
            .subscribe({
              next: () => {
                // Remove loading message
                this.msg.remove(loadingMsg.messageId);

                // Clear saved form data after successful unlock
                this.clearSavedFormData();
                this.form.reset({ id: this.id });
                this.isVisible.unlock = false;
                // Call reload only if dataEditActionsComponent exists (in detail view)
                if (this.dataEditActionsComponent) {
                  this.dataEditActionsComponent.reload();
                }

                this.loadLatestDeclarationInfo();
                this.form.get('date')?.setValue(new Date());
                this.isVisibleChange.emit(this.isVisible);
                this.msg.success('SST Return unlocked successfully.');
              },
              error: err => {
                // Remove loading message
                this.msg.remove(loadingMsg.messageId);
                console.error('Error unlocking SST return:', err);
                this.msg.error(
                  'Failed to unlock SST Return. Please try again later.'
                );
              }
            });
        }
      });
    });
  }

  cancel() {
    // Save the current form values before closing
    this.saveFormValues();

    // Don't reset the form - we'll preserve values for next opening
    // Just update the visibility
    this.isVisible.submit = false;
    this.isVisible.unlock = false;
    this.isVisibleChange.emit(this.isVisible);
  }
}
